/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Async state management sagas
 *
 * @author Yakov Gushchin
 * @version 1.12.0
 * @since 1.11.0
 */

import clone from 'lodash/clone';
import { SagaIterator, Task } from 'redux-saga';
import { all, cancel, fork, put, select, takeEvery } from 'redux-saga/effects';

import { globalAppStore } from '../../globalAppStore';
import { injectAsyncReducer } from '../../reducers/rootReducer';
import {
    asyncStateManagementInitSuccessAction,
    asyncStateManagementSaveAction,
    asyncStateManagementUnmountSuccessAction,
} from './asyncStateManagementActions';
import { ASYNC_STATE_MANAGEMENT_INIT, ASYNC_STATE_MANAGEMENT_UNMOUNT } from './asyncStateManagementConstants';
import { makeSelectAsyncStateManagementActiveSagas } from './asyncStateManagementSelectors';
import { TAsyncStateManagementInitAction, TAsyncStateManagementUnmountAction } from './asyncStateManagementTypes';

/*
* -------------------------------------------
*         Page init and unmount
* -------------------------------------------
*/

export function* asyncStateManagementInitSaga(action: TAsyncStateManagementInitAction): SagaIterator {
    // fork saga
    const sagas = action.payload.sagas;
    if (sagas) {
        const currentSaga: Task = yield fork(sagas);
        yield put(asyncStateManagementSaveAction({ id: action.payload.id, task: clone(currentSaga) }));
    }

    // combine reducers
    if (action.payload.reducer) {
        Object.entries(action.payload.reducer).forEach((entry: [string, any]) => {
            injectAsyncReducer(globalAppStore, entry[0], entry[1]);
        });
    }

    // show component on success
    yield put(asyncStateManagementInitSuccessAction({ id: action.payload.id }));
}

export function* asyncStateManagementUnmountSaga(action: TAsyncStateManagementUnmountAction): SagaIterator {
    const activeSagasTask = yield select(makeSelectAsyncStateManagementActiveSagas());
    const taskToCancel = activeSagasTask[action.payload];
    if (taskToCancel) {
        yield cancel(taskToCancel);
        yield put(asyncStateManagementUnmountSuccessAction(action.payload));
    }
}

/*
* -------------------------------------------
*         Saga summary
* -------------------------------------------
*/

export function* asyncStateManagementSagas(): SagaIterator {
    yield all([
        takeEvery(ASYNC_STATE_MANAGEMENT_INIT, asyncStateManagementInitSaga),
        takeEvery(ASYNC_STATE_MANAGEMENT_UNMOUNT, asyncStateManagementUnmountSaga),
    ]);
}
